import { WebhookSignal } from '../types/webhook';

// Free signal sources configuration
export const SIGNAL_SOURCES = {
  CRYPTO_COMPARE: 'https://min-api.cryptocompare.com/data/v2/news/',
  COIN_GECKO: 'https://api.coingecko.com/api/v3/simple/price',
  BINANCE: 'https://api.binance.com/api/v3/ticker/24hr',
  BINANCE_WS: 'wss://stream.binance.com:9443/ws/btcusdt@ticker',
  COINCAP: 'https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=10&page=1',
  CRYPTO_PANIC: 'https://cryptopanic.com/api/v1/posts/',
  MOCK_SIGNALS: 'mock' // For testing
};

export class SignalGenerator {
  private isRunning = false;
  private intervalId: NodeJS.Timeout | null = null;
  private mockSignalsEnabled = true; // Can be toggled

  // Generate mock signals for testing
  public generateMockSignal(): WebhookSignal {
    const symbols = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'ADAUSDT', 'BNBUSDT'];
    const types = ['buy', 'sell', 'alert', 'update'];
    const timeframes = ['1', '5', '15', '30', '60', '240', 'D'];
    
    const symbol = symbols[Math.floor(Math.random() * symbols.length)];
    const type = types[Math.floor(Math.random() * types.length)];
    const timeframe = timeframes[Math.floor(Math.random() * timeframes.length)];
    const confidence = Math.random();
    
    // Generate realistic price levels based on symbol
    const getRealisticPrice = (symbol: string) => {
      const priceRanges: { [key: string]: [number, number] } = {
        'BTCUSDT': [40000, 70000],
        'ETHUSDT': [2000, 5000],
        'SOLUSDT': [80, 200],
        'ADAUSDT': [0.3, 1.5],
        'BNBUSDT': [200, 600],
        'DOGEUSDT': [0.05, 0.3],
        'XRPUSDT': [0.3, 1.2],
        'MATICUSDT': [0.5, 2.0],
        'AVAXUSDT': [15, 50],
        'DOTUSDT': [5, 25]
      };
      
      const range = priceRanges[symbol] || [1, 100];
      return range[0] + Math.random() * (range[1] - range[0]);
    };
    
    const basePrice = getRealisticPrice(symbol);
    const buyAt = basePrice * (0.98 + Math.random() * 0.04); // ±2% from base
    const takeProfit = buyAt * (1.02 + Math.random() * 0.08); // 2-10% profit
    const stopLoss = buyAt * (0.90 + Math.random() * 0.08); // 2-10% loss
    
    // Debug logging
    console.log(`Generated signal for ${symbol}:`, {
      basePrice: basePrice.toFixed(2),
      buyAt: buyAt.toFixed(2),
      takeProfit: takeProfit.toFixed(2),
      stopLoss: stopLoss.toFixed(2)
    });
    
    const messages = {
      buy: ['Strong buy signal', 'Bullish momentum detected', 'Buy opportunity'],
      sell: ['Sell signal triggered', 'Bearish trend confirmed', 'Take profit'],
      alert: ['Price alert triggered', 'Volume spike detected', 'Pattern completion'],
      update: ['Chart updated', 'New data available', 'Market update']
    };

    return {
      id: `mock-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      type: type as any,
      symbol,
      timeframe,
      price: basePrice,
      buyAt: type === 'buy' ? buyAt : undefined,
      takeProfit: type === 'buy' ? takeProfit : undefined,
      stopLoss: type === 'buy' ? stopLoss : undefined,
      message: messages[type as keyof typeof messages][Math.floor(Math.random() * 3)],
      source: 'mock-generator',
      confidence,
      metadata: {
        rsi: Math.floor(Math.random() * 100),
        macd: Math.random() > 0.5 ? 'bullish' : 'bearish',
        volume: Math.floor(Math.random() * 1000000),
        riskReward: takeProfit && stopLoss ? ((takeProfit - buyAt) / (buyAt - stopLoss)).toFixed(2) : undefined
      }
    };
  }

  // Fetch signals from CryptoCompare
  public async fetchCryptoCompareSignals(): Promise<WebhookSignal[]> {
    try {
      const response = await fetch(SIGNAL_SOURCES.CRYPTO_COMPARE);
      const data = await response.json();
      
      if (data.Response === 'Success' && data.Data) {
        return data.Data.slice(0, 5).map((news: any, index: number) => ({
          id: `cc-${news.id}`,
          timestamp: news.published_on * 1000,
          type: 'alert',
          symbol: this.extractSymbolFromTitle(news.title) || 'BTCUSDT',
          message: news.title,
          source: 'cryptocompare',
          confidence: 0.7,
          metadata: {
            url: news.url,
            source: news.source,
            tags: news.tags
          }
        }));
      }
    } catch (error) {
      console.warn('Failed to fetch CryptoCompare signals:', error);
    }
    
    return [];
  }

  // Fetch signals from CoinGecko
  public async fetchCoinGeckoSignals(): Promise<WebhookSignal[]> {
    try {
      const response = await fetch(`${SIGNAL_SOURCES.COIN_GECKO}?ids=bitcoin,ethereum,solana,cardano&vs_currencies=usd&include_24hr_change=true`);
      const data = await response.json();
      
      const signals: WebhookSignal[] = [];
      
      Object.entries(data).forEach(([coinId, priceData]: [string, any]) => {
        const symbol = this.coinIdToSymbol(coinId);
        const change24h = priceData.usd_24h_change;
        
        if (Math.abs(change24h) > 5) { // Significant price change
          const currentPrice = priceData.usd;
          const isBuy = change24h > 0;
          
          // Generate trading levels for buy signals
          let buyAt, takeProfit, stopLoss;
          if (isBuy) {
            buyAt = currentPrice * (0.99 + Math.random() * 0.02); // 1-3% below current
            takeProfit = buyAt * (1.03 + Math.random() * 0.07); // 3-10% profit
            stopLoss = buyAt * (0.92 + Math.random() * 0.06); // 2-8% loss
          }
          
          signals.push({
            id: `cg-${coinId}-${Date.now()}`,
            timestamp: Date.now(),
            type: isBuy ? 'buy' : 'sell',
            symbol,
            price: currentPrice,
            buyAt,
            takeProfit,
            stopLoss,
            message: `${symbol} ${isBuy ? 'gained' : 'lost'} ${Math.abs(change24h).toFixed(2)}% in 24h`,
            source: 'coingecko',
            confidence: Math.min(Math.abs(change24h) / 20, 1), // Higher confidence for larger changes
            metadata: {
              price: priceData.usd,
              change24h,
              coinId
            }
          });
        }
      });
      
      return signals;
    } catch (error) {
      console.warn('Failed to fetch CoinGecko signals:', error);
    }
    
    return [];
  }

  // Fetch signals from Binance
  public async fetchBinanceSignals(): Promise<WebhookSignal[]> {
    try {
      const response = await fetch(SIGNAL_SOURCES.BINANCE);
      const data = await response.json();
      
      const signals: WebhookSignal[] = [];
      
      data.slice(0, 10).forEach((ticker: any) => {
        const priceChange = parseFloat(ticker.priceChangePercent);
        
        if (Math.abs(priceChange) > 3) { // Significant price change
          const currentPrice = parseFloat(ticker.lastPrice);
          const isBuy = priceChange > 0;
          
          // Generate trading levels for buy signals
          let buyAt, takeProfit, stopLoss;
          if (isBuy) {
            buyAt = currentPrice * (0.99 + Math.random() * 0.02); // 1-3% below current
            takeProfit = buyAt * (1.03 + Math.random() * 0.07); // 3-10% profit
            stopLoss = buyAt * (0.92 + Math.random() * 0.06); // 2-8% loss
          }
          
          signals.push({
            id: `binance-${ticker.symbol}-${Date.now()}`,
            timestamp: Date.now(),
            type: isBuy ? 'buy' : 'sell',
            symbol: ticker.symbol,
            price: currentPrice,
            buyAt,
            takeProfit,
            stopLoss,
            message: `${ticker.symbol} ${isBuy ? 'pumped' : 'dumped'} ${Math.abs(priceChange).toFixed(2)}%`,
            source: 'binance',
            confidence: Math.min(Math.abs(priceChange) / 15, 1),
            metadata: {
              volume: ticker.volume,
              high: ticker.highPrice,
              low: ticker.lowPrice,
              count: ticker.count
            }
          });
        }
      });
      
      return signals;
    } catch (error) {
      console.warn('Failed to fetch Binance signals:', error);
    }
    
    return [];
  }

  // Fetch real-time signals from CoinGecko markets
  public async fetchCoinCapSignals(): Promise<WebhookSignal[]> {
    try {
      const response = await fetch(SIGNAL_SOURCES.COINCAP);
      const data = await response.json();
      
      const signals: WebhookSignal[] = [];
      
      if (Array.isArray(data)) {
        data.slice(0, 10).forEach((coin: any) => {
          const changePercent = coin.price_change_percentage_24h || 0;
          const currentPrice = coin.current_price || 0;
          
          if (Math.abs(changePercent) > 2) { // 2%+ change
            const isBuy = changePercent > 0;
            
            // Generate trading levels for buy signals
            let buyAt, takeProfit, stopLoss;
            if (isBuy) {
              buyAt = currentPrice * (0.99 + Math.random() * 0.02); // 1-3% below current
              takeProfit = buyAt * (1.03 + Math.random() * 0.07); // 3-10% profit
              stopLoss = buyAt * (0.92 + Math.random() * 0.06); // 2-8% loss
            }
            
            signals.push({
              id: `coingecko-${coin.id}-${Date.now()}`,
              timestamp: Date.now(),
              type: isBuy ? 'buy' : 'sell',
              symbol: `${coin.symbol.toUpperCase()}USDT`,
              price: currentPrice,
              buyAt,
              takeProfit,
              stopLoss,
              message: `${coin.symbol.toUpperCase()} ${isBuy ? 'gained' : 'lost'} ${Math.abs(changePercent).toFixed(2)}% in 24h`,
              source: 'coingecko',
              confidence: Math.min(Math.abs(changePercent) / 15, 1),
              metadata: {
                rank: coin.market_cap_rank,
                volume: coin.total_volume,
                marketCap: coin.market_cap,
                changePercent24h: changePercent
              }
            });
          }
        });
      }
      
      return signals;
    } catch (error) {
      console.warn('Failed to fetch CoinGecko market signals:', error);
      return [];
    }
  }

  // Start generating signals
  public startSignalGeneration(intervalMs: number = 30000) {
    if (this.isRunning) return;
    
    this.isRunning = true;
    this.intervalId = setInterval(async () => {
      try {
        // Generate mock signals (reduced frequency)
        if (this.mockSignalsEnabled && Math.random() < 0.3) {
          const mockSignal = this.generateMockSignal();
          this.sendSignal(mockSignal);
        }
        
        // Fetch real signals automatically (increased frequency)
        const cgSignals = await this.fetchCoinGeckoSignals();
        cgSignals.forEach(signal => this.sendSignal(signal));
        
        // Fetch CoinCap signals (real-time data)
        const coinCapSignals = await this.fetchCoinCapSignals();
        coinCapSignals.forEach(signal => this.sendSignal(signal));
        
        // Fetch Binance signals more frequently
        if (Math.random() < 0.5) {
          const binanceSignals = await this.fetchBinanceSignals();
          binanceSignals.forEach(signal => this.sendSignal(signal));
        }
        
      } catch (error) {
        console.warn('Error generating signals:', error);
      }
    }, intervalMs);
  }

  // Stop generating signals
  public stopSignalGeneration() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isRunning = false;
  }

  public toggleMockSignals(enabled: boolean) {
    this.mockSignalsEnabled = enabled;
    console.log(`Mock signals ${enabled ? 'enabled' : 'disabled'}`);
  }

  // Send signal to webhook system
  private sendSignal(signal: WebhookSignal) {
    try {
      // Get existing signals
      const existingData = localStorage.getItem('tradingview-webhook-data');
      const signals = existingData ? JSON.parse(existingData) : [];
      
      // Add new signal
      signals.push(signal);
      
      // Keep only last 50 signals
      if (signals.length > 50) {
        signals.splice(0, signals.length - 50);
      }
      
      // Save back to localStorage
      localStorage.setItem('tradingview-webhook-data', JSON.stringify(signals));
      
      console.log('Signal sent:', signal);
    } catch (error) {
      console.warn('Failed to send signal:', error);
    }
  }

  // Helper methods
  private extractSymbolFromTitle(title: string): string | null {
    const symbols = ['BTC', 'ETH', 'SOL', 'ADA', 'BNB', 'XRP', 'DOGE', 'MATIC'];
    const upperTitle = title.toUpperCase();
    
    for (const symbol of symbols) {
      if (upperTitle.includes(symbol)) {
        return symbol + 'USDT';
      }
    }
    
    return null;
  }

  private coinIdToSymbol(coinId: string): string {
    const mapping: { [key: string]: string } = {
      'bitcoin': 'BTCUSDT',
      'ethereum': 'ETHUSDT',
      'solana': 'SOLUSDT',
      'cardano': 'ADAUSDT',
      'binancecoin': 'BNBUSDT',
      'ripple': 'XRPUSDT',
      'dogecoin': 'DOGEUSDT',
      'matic-network': 'MATICUSDT'
    };
    
    return mapping[coinId] || coinId.toUpperCase() + 'USDT';
  }
}

export const signalGenerator = new SignalGenerator();
